// Sidebar line icons — one shared 24×24 / stroke-1.6 set so every rail
// item and menu row reads as one system. Colour comes from currentColor,
// so active/hover states are driven entirely by the nav CSS.

import React from "react";

export type NavIconName =
  | "overview"
  | "analytics"
  | "keys"
  | "subscription"
  | "credits"
  | "dashboard"
  | "home"
  | "usage"
  | "devices"
  | "assets"
  | "governance"
  | "panel"
  | "hide"
  | "seats"
  | "models"
  | "observability"
  | "budgets"
  | "settings"
  | "leaderboard"
  | "trophy"
  | "resources"
  | "account"
  | "terms"
  | "help"
  | "terminal"
  | "copy"
  | "check"
  | "logout";

const PATHS: Record<NavIconName, React.ReactNode> = {
  devices: (
    <>
      <rect x="3" y="4.5" width="18" height="11.5" rx="1.8" />
      <path d="M8 20h8" />
      <path d="M12 16v4" />
    </>
  ),
  overview: (
    <>
      <rect x="3" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="3" width="7" height="7" rx="1.5" />
      <rect x="3" y="14" width="7" height="7" rx="1.5" />
      <rect x="14" y="14" width="7" height="7" rx="1.5" />
    </>
  ),
  analytics: (
    <>
      <path d="M4 20V4" />
      <path d="M4 20h16" />
      <rect x="6.6" y="13" width="3" height="7" rx="0.8" />
      <rect x="11" y="9.5" width="3" height="10.5" rx="0.8" />
      <rect x="15.4" y="6" width="3" height="14" rx="0.8" />
    </>
  ),
  keys: (
    <>
      <circle cx="7.5" cy="15.5" r="4.5" />
      <path d="M10.7 12.3 20 3" />
      <path d="m15.5 7.5 3 3" />
      <path d="M18 5l2.5 2.5" />
    </>
  ),
  subscription: (
    <>
      <rect x="2.5" y="5.5" width="19" height="13" rx="2.2" />
      <path d="M2.5 9.5h19" />
      <path d="M6 14.5h4" />
    </>
  ),
  credits: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 8v8" />
      <path d="M8 12h8" />
    </>
  ),
  dashboard: (
    <>
      <path d="M4 13a8 8 0 0 1 16 0" />
      <path d="M12 13 7.8 10.4" />
      <circle cx="12" cy="13" r="1.3" fill="currentColor" stroke="none" />
    </>
  ),
  usage: (
    <>
      <circle cx="12" cy="8" r="3.4" />
      <path d="M4.5 20a7.5 7.5 0 0 1 15 0" />
      <path d="M16.5 4.5 19 2" />
    </>
  ),
  home: (
    <>
      <path d="M3.5 10.5 12 3.5l8.5 7" />
      <path d="M5.5 9v10.5h13V9" />
      <path d="M10 19.5v-5h4v5" />
    </>
  ),
  assets: (
    <>
      <circle cx="12" cy="6.5" r="3" />
      <rect x="3.5" y="13.5" width="6.5" height="6.5" rx="1.5" />
      <path d="M17.2 13.2 21 20h-7.6z" />
    </>
  ),
  governance: (
    <>
      <path d="M12 3 4.5 6v5.5c0 4.4 3.1 7.6 7.5 9.5 4.4-1.9 7.5-5.1 7.5-9.5V6z" />
      <path d="m9 12 2.2 2.2L15.5 10" />
    </>
  ),
  panel: (
    <>
      <rect x="3" y="4" width="18" height="16" rx="2.5" />
      <path d="M9 4v16" />
    </>
  ),
  hide: (
    <>
      <path d="m11 7-5 5 5 5" />
      <path d="m18 7-5 5 5 5" />
    </>
  ),
  seats: (
    <>
      <circle cx="8" cy="8" r="3" />
      <path d="M2.5 19a5.5 5.5 0 0 1 11 0" />
      <circle cx="16.5" cy="9" r="2.5" />
      <path d="M15 14.2a5 5 0 0 1 6.5 4.8" />
    </>
  ),
  observability: (
    <>
      <path d="M2.5 12S6 6 12 6s9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z" />
      <circle cx="12" cy="12" r="2.6" />
    </>
  ),
  budgets: (
    <>
      <path d="M3.5 16a8.5 8.5 0 0 1 17 0" />
      <path d="M12 16l4.2-5" />
      <circle cx="12" cy="16" r="1.4" />
      <path d="M3.5 20h17" />
    </>
  ),
  models: (
    <>
      <path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" />
      <path d="m3.3 7 8.7 5 8.7-5" />
      <path d="M12 22V12" />
    </>
  ),
  settings: (
    <>
      <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
  leaderboard: (
    <>
      <rect x="10" y="4" width="4" height="16" rx="1" />
      <rect x="4" y="10" width="4" height="10" rx="1" />
      <rect x="16" y="8" width="4" height="12" rx="1" />
    </>
  ),
  trophy: (
    <>
      <path d="M7 4h10v5a5 5 0 0 1-10 0Z" />
      <path d="M7 6H4.5a2.5 2.5 0 0 0 2.6 3.5M17 6h2.5a2.5 2.5 0 0 1-2.6 3.5" />
      <path d="M12 14v3.5M8.5 20.5h7M9.5 17.5h5v3h-5z" />
    </>
  ),
  resources: (
    <>
      <path d="M12 6.6v13.2" />
      <path d="M12 6.6C10 5.3 7.3 4.7 5.2 4.7c-.8 0-1.2.1-1.2.1v12.9s.5-.1 1.3-.1c2.2 0 4.8.6 6.7 1.9" />
      <path d="M12 6.6c2-1.3 4.7-1.9 6.8-1.9.8 0 1.2.1 1.2.1v12.9s-.5-.1-1.3-.1c-2.2 0-4.8.6-6.7 1.9" />
    </>
  ),
  account: (
    <>
      <circle cx="12" cy="8.5" r="3.6" />
      <path d="M5 19.5a7 7 0 0 1 14 0" />
    </>
  ),
  terms: (
    <>
      <path d="M6 3h7l5 5v12a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z" />
      <path d="M13 3v5h5" />
      <path d="M8.5 13h7" />
      <path d="M8.5 16.5h7" />
      <path d="M8.5 9.5h3" />
    </>
  ),
  help: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M9.4 9.3a2.7 2.7 0 0 1 5.2 1c0 1.8-2.6 2.3-2.6 4" />
      <path d="M12 17.2v.1" />
    </>
  ),
  terminal: (
    <>
      <rect x="3" y="4.5" width="18" height="15" rx="2.5" />
      <path d="M7.5 9.5l3 2.5-3 2.5" />
      <path d="M13 15h3.5" />
    </>
  ),
  copy: (
    <>
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </>
  ),
  check: <polyline points="20 6 9 17 4 12" />,
  logout: (
    <>
      <path d="M14 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8" />
      <path d="M17 8l4 4-4 4" />
      <path d="M21 12H9" />
    </>
  ),
};

export function NavIcon({ name }: { name: NavIconName }) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {PATHS[name]}
    </svg>
  );
}
